<template>
  <v-card variant="outlined" class="ncrm-privacy-settings pa-4">
    <div class="d-flex align-start justify-space-between flex-wrap ga-2 mb-3">
      <div>
        <h3 class="text-subtitle-1 font-weight-bold">{{ $t(`${copyKey}.title`) }}</h3>
        <p class="text-body-2 text-medium-emphasis mb-0">{{ $t(`${copyKey}.subtitle`) }}</p>
      </div>
      <v-chip prepend-icon="mdi-shield-lock-outline">
        {{ $t(`${copyKey}.enforcement`, { mode: $t(`${copyKey}.modes.${settings.enforcement_mode}`) }) }}
      </v-chip>
    </div>

    <v-alert v-if="errorMessage" type="error" density="compact" class="mb-3">{{ errorMessage }}</v-alert>
    <div class="ncrm-privacy-settings__fields">
      <v-select v-model="form.unknown_policy" :items="policyItems" :label="$t(`${copyKey}.unknownPolicy`)"
        :hint="$t(`${copyKey}.unknownPolicyHint`)" persistent-hint :disabled="!canManage" />
      <v-switch v-model="form.evidence_attachment_required" color="primary" inset :disabled="!canManage"
        :label="$t(`${copyKey}.attachmentRequired`)" :hint="$t(`${copyKey}.attachmentRequiredHint`)" persistent-hint />
      <div>
        <v-switch v-model="form.consent_invitations_enabled" color="primary" inset :disabled="!canManage"
          :label="$t(`${copyKey}.invitations`)" :hint="$t(`${copyKey}.invitationsHint`)" persistent-hint />
        <p v-if="invitationsChangedAt" class="text-caption text-medium-emphasis mt-1 mb-0">
          {{ $t(`${copyKey}.invitationsChangedAt`, { at: invitationsChangedAt }) }}
        </p>
      </div>
    </div>
    <p class="text-caption text-medium-emphasis mt-3 mb-0">{{ $t(`${copyKey}.enforcementHint`) }}</p>
    <div v-if="canManage" class="mt-3">
      <v-btn color="primary" variant="flat" :loading="saving" :disabled="!changed" @click="save">{{ $t(`${copyKey}.save`) }}</v-btn>
    </div>
    <p v-else class="text-caption text-medium-emphasis mt-3 mb-0">{{ $t(`${copyKey}.readOnly`) }}</p>
    <NcrmPrivacyInvitationAttestationDialog v-model="attestationOpen" :saving="saving" :error="attestationError"
      @confirm="persist({ ...changes, consent_invitations_attestation: true })" />
  </v-card>
</template>

<script>
import { buildDateTimePartsFromDate, formatDateTimeFieldDisplay } from "@/Utile/dateTimeField.mjs";
import NcrmPrivacyInvitationAttestationDialog from "./NcrmPrivacyInvitationAttestationDialog.vue";
import { savePrivacySettings } from "./privacyPurposeCatalogApi.mjs";
import { UNKNOWN_POLICIES } from "./privacyPurposeModel.mjs";

const EDITABLE = Object.freeze(["unknown_policy", "evidence_attachment_required", "consent_invitations_enabled"]);

export default {
  name: "NcrmPrivacySettingsCard",
  components: { NcrmPrivacyInvitationAttestationDialog },
  props: {
    settings: { type: Object, required: true },
    canManage: { type: Boolean, default: false },
  },
  emits: ["saved"],
  data: () => ({
    form: { unknown_policy: null, evidence_attachment_required: false, consent_invitations_enabled: false },
    saving: false, errorMessage: "", attestationOpen: false, attestationError: "",
  }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace.purposes.settings"; },
    policyItems() {
      return UNKNOWN_POLICIES.map((value) => ({
        value, title: this.$t(`ncrm.contacts.dataProtection.workspace.purposes.policies.${value}`),
      }));
    },
    // Numai câmpurile schimbate se trimit; `enforcement_mode` nu se trimite niciodată.
    changes() {
      return Object.fromEntries(EDITABLE
        .filter((key) => this.form[key] !== this.settings[key])
        .map((key) => [key, this.form[key]]));
    },
    changed() { return Object.keys(this.changes).length > 0; },
    invitationsChangedAt() {
      const value = this.settings.consent_invitations_changed_at;
      const date = value ? new Date(value) : null;
      return date && !Number.isNaN(date.getTime()) ? formatDateTimeFieldDisplay(buildDateTimePartsFromDate(date)) : "";
    },
  },
  watch: {
    settings: {
      immediate: true,
      handler(value) {
        this.form = Object.fromEntries(EDITABLE.map((key) => [key, value[key] ?? false]));
      },
    },
  },
  methods: {
    // Activarea invitațiilor (`GDPR-099`) trece întâi prin declarația companiei; oprirea, nu.
    save() {
      if (this.changes.consent_invitations_enabled === true) {
        this.attestationError = "";
        this.attestationOpen = true;
        return;
      }
      this.persist(this.changes);
    },
    async persist(changes) {
      this.saving = true; this.errorMessage = ""; this.attestationError = "";
      try {
        await savePrivacySettings(changes);
        this.attestationOpen = false;
        this.$emit("saved");
      } catch {
        const message = this.$t(`${this.copyKey}.saveError`);
        if (this.attestationOpen) this.attestationError = message;
        else this.errorMessage = message;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-settings { text-align: left; }
.ncrm-privacy-settings__fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; }
</style>
